import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const handleSubmit = async () => {
    if (!subject.trim() || !description.trim()) {
      toast.error('Por favor, complete todos los campos');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('smc_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subject,
          description,
          page_url: window.location.href,
          metadata: {
            user_agent: navigator.userAgent,
            timestamp: new Date().toISOString()
          }
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al enviar feedback');
      }

      toast.success('¡Gracias! Tu feedback ha sido enviado correctamente');
      setOpen(false);
      setSubject('');
      setDescription('');
    } catch (error) {
      toast.error(error.message || 'Error al enviar feedback');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        data-testid="feedback-button"
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          background: '#2563eb',
          color: 'white',
          borderRadius: '50%',
          width: '56px',
          height: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 10px 40px rgba(37, 99, 235, 0.4)',
          border: '2px solid #1d4ed8',
          zIndex: 1000,
          transition: 'all 0.3s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 15px 50px rgba(37, 99, 235, 0.6)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 10px 40px rgba(37, 99, 235, 0.4)';
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
        </svg>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]" style={{ background: '#1e293b', border: '1px solid #334155' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#f1f5f9', fontSize: '18px', fontWeight: 600 }}>
              Enviar Feedback / Reportar Error
            </DialogTitle>
          </DialogHeader>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '10px 0' }}>
            {/* Info message */}
            <div style={{ 
              padding: '12px', 
              background: '#0ea5e920', 
              border: '1px solid #0ea5e9', 
              borderRadius: '8px',
              display: 'flex',
              gap: '10px'
            }}>
              <span style={{ color: '#22d3ee', fontSize: '16px' }}>💡</span>
              <div style={{ flex: 1 }}>
                <p style={{ color: '#e0f2fe', fontSize: '13px', lineHeight: '1.5', margin: 0 }}>
                  Tu mensaje será enviado directamente a <strong>dredondo@valorizasm.com</strong>.
                  Responderemos lo antes posible.
                </p>
              </div>
            </div>

            {/* Subject */}
            <div>
              <Label htmlFor="feedback-subject" style={{ color: '#cbd5e1', fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                Asunto *
              </Label>
              <Input
                id="feedback-subject"
                type="text"
                placeholder="Ej: Error al exportar datos, Sugerencia de mejora..."
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="feedback-subject-input"
                style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', height: '40px' }}
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="feedback-description" style={{ color: '#cbd5e1', fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                Descripción detallada *
              </Label>
              <Textarea
                id="feedback-description"
                placeholder="Describe el problema o tu sugerencia con el mayor detalle posible. Incluye pasos para reproducir el error si es aplicable."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                data-testid="feedback-description-input"
                style={{ 
                  background: '#0f172a', 
                  border: '1px solid #334155', 
                  color: '#f1f5f9', 
                  resize: 'vertical',
                  minHeight: '120px'
                }}
              />
            </div>

            {/* Auto-captured info */}
            <div style={{ 
              padding: '10px', 
              background: '#0f172a', 
              border: '1px solid #334155',
              borderRadius: '6px' 
            }}>
              <p style={{ color: '#64748b', fontSize: '11px', margin: 0 }}>
                ℹ️ Se incluirá automáticamente: página actual, fecha/hora y tu usuario ({user?.email})
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={loading}
              style={{ color: '#64748b' }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              data-testid="feedback-submit-button"
              style={{
                background: '#2563eb',
                color: 'white',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Enviando...' : 'Enviar Feedback'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
